/**
 * Support Service
 *
 * GraphQL operations for client logs and support requests.
 * Connects to the centralized backend (database.teknology.eu).
 */

import type {
  ClientLog,
  CreateClientLogInput,
  ClientLogFilters,
  SupportRequest,
  CreateSupportRequestInput,
  UpdateSupportRequestInput,
  SupportRequestFilters,
  SupportStats,
} from '../domain/support';

// =============================================================================
// CENTRALIZED API CLIENT (uses database.teknology.eu)
// =============================================================================

/**
 * Get the API endpoint URL for support operations.
 * Uses the centralized GraphQL backend (database.teknology.eu).
 */
function getApiEndpoint(): string {
  // Use centralized backend from environment or default
  if (typeof window !== 'undefined') {
    // Browser: use configured VITE_GRAPHQL_URL or default
    return (window as any).__ENV__?.VITE_GRAPHQL_URL ||
           import.meta.env?.VITE_GRAPHQL_URL ||
           'https://database.teknology.eu/graphql';
  }
  // Node.js (SSR): use environment variable or default
  return process.env.VITE_GRAPHQL_URL || 'https://database.teknology.eu/graphql';
}

/**
 * Get auth token for the centralized backend.
 * Priority:
 * 1. User JWT from sessionStorage (for frontend user requests)
 * 2. API token from environment variable (for admin panel)
 */
function getAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    // First try user JWT from sessionStorage
    const userToken = sessionStorage.getItem('auth_access_token');
    if (userToken) {
      return userToken;
    }

    // Fallback to API token from environment (for admin apps)
    const apiToken = (window as any).__ENV__?.VITE_GRAPHQL_TOKEN ||
                     import.meta.env?.VITE_GRAPHQL_TOKEN;
    if (apiToken) {
      return apiToken;
    }
  }

  // Server-side fallback
  return process.env.VITE_GRAPHQL_TOKEN || null;
}

/**
 * Execute a GraphQL request to the centralized backend
 */
async function centralGraphQL<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const endpoint = getApiEndpoint();
  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL HTTP error: ${response.status}`);
  }

  const result = await response.json();

  if (result.errors && result.errors.length > 0) {
    const errorMessage = result.errors.map((e: { message: string }) => e.message).join(', ');
    throw new Error(`GraphQL error: ${errorMessage}`);
  }

  return result.data;
}

// =============================================================================
// GRAPHQL QUERIES / MUTATIONS
// =============================================================================

// -----------------------------------------------------------------------------
// CLIENT LOGS
// -----------------------------------------------------------------------------

const CREATE_CLIENT_LOG = `
  mutation CreateClientLog($input: CreateClientLogInput!) {
    createClientLog(input: $input) {
      id
      userId
      accountId
      level
      source
      message
      stack
      url
      userAgent
      metadata
      timestamp
      dismissed
    }
  }
`;

const CREATE_CLIENT_LOGS_BATCH = `
  mutation CreateClientLogsBatch($inputs: [CreateClientLogInput!]!) {
    createClientLogsBatch(inputs: $inputs) {
      count
    }
  }
`;

const GET_CLIENT_LOGS = `
  query GetClientLogs($filters: ClientLogFilters, $limit: Int, $offset: Int) {
    clientLogs(filters: $filters, limit: $limit, offset: $offset) {
      items {
        id
        userId
        accountId
        level
        source
        message
        stack
        url
        userAgent
        metadata
        timestamp
        dismissed
      }
      total
    }
  }
`;

const DISMISS_CLIENT_LOG = `
  mutation DismissClientLog($id: ID!, $dismissed: Boolean!) {
    dismissClientLog(id: $id, dismissed: $dismissed) {
      id
      dismissed
    }
  }
`;

const DISMISS_CLIENT_LOGS_BATCH = `
  mutation DismissClientLogsBatch($ids: [ID!]!, $dismissed: Boolean!) {
    dismissClientLogsBatch(ids: $ids, dismissed: $dismissed) {
      count
    }
  }
`;

// -----------------------------------------------------------------------------
// SUPPORT REQUESTS
// -----------------------------------------------------------------------------

const CREATE_SUPPORT_REQUEST = `
  mutation CreateSupportRequest($input: CreateSupportRequestInput!) {
    createSupportRequest(input: $input) {
      id
      userId
      userEmail
      userName
      accountId
      type
      priority
      status
      subject
      description
      attachmentUrl
      browserInfo
      currentUrl
      relatedLogIds
      adminNotes
      createdAt
      updatedAt
    }
  }
`;

const GET_SUPPORT_REQUESTS = `
  query GetSupportRequests($filters: SupportRequestFilters, $limit: Int, $offset: Int) {
    supportRequests(filters: $filters, limit: $limit, offset: $offset) {
      items {
        id
        userId
        userEmail
        userName
        accountId
        type
        priority
        status
        subject
        description
        attachmentUrl
        browserInfo
        currentUrl
        relatedLogIds
        adminNotes
        createdAt
        updatedAt
      }
      total
    }
  }
`;

const GET_SUPPORT_REQUEST = `
  query GetSupportRequest($id: ID!) {
    supportRequest(id: $id) {
      id
      userId
      userEmail
      userName
      accountId
      type
      priority
      status
      subject
      description
      attachmentUrl
      browserInfo
      currentUrl
      relatedLogIds
      adminNotes
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_SUPPORT_REQUEST = `
  mutation UpdateSupportRequest($id: ID!, $input: UpdateSupportRequestInput!) {
    updateSupportRequest(id: $id, input: $input) {
      id
      status
      priority
      adminNotes
      updatedAt
    }
  }
`;

const GET_SUPPORT_STATS = `
  query GetSupportStats {
    supportStats {
      openRequests
      byType
      byPriority
      byStatus
      recentErrors
      recentWarnings
    }
  }
`;

// =============================================================================
// SERVICE FUNCTIONS
// =============================================================================

/**
 * Create a single client log entry
 */
export async function createClientLog(input: CreateClientLogInput): Promise<ClientLog> {
  const result = await centralGraphQL<{ createClientLog: ClientLog }>(
    CREATE_CLIENT_LOG,
    { input }
  );
  return result.createClientLog;
}

/**
 * Create multiple client logs in a batch (for efficiency)
 */
export async function createClientLogsBatch(inputs: CreateClientLogInput[]): Promise<number> {
  const result = await centralGraphQL<{ createClientLogsBatch: { count: number } }>(
    CREATE_CLIENT_LOGS_BATCH,
    { inputs }
  );
  return result.createClientLogsBatch?.count ?? 0;
}

/**
 * Get client logs with optional filters and pagination
 */
export async function getClientLogs(
  filters?: ClientLogFilters,
  limit = 50,
  offset = 0
): Promise<{ items: ClientLog[]; total: number }> {
  const result = await centralGraphQL<{
    clientLogs: { items: ClientLog[]; total: number };
  }>(GET_CLIENT_LOGS, { filters, limit, offset });
  return result.clientLogs ?? { items: [], total: 0 };
}

/**
 * Dismiss (archive) a client log
 */
export async function dismissClientLog(id: string, dismissed = true): Promise<ClientLog> {
  const result = await centralGraphQL<{ dismissClientLog: ClientLog }>(
    DISMISS_CLIENT_LOG,
    { id, dismissed }
  );
  return result.dismissClientLog;
}

/**
 * Dismiss multiple client logs in a batch
 */
export async function dismissClientLogsBatch(ids: string[], dismissed = true): Promise<number> {
  const result = await centralGraphQL<{ dismissClientLogsBatch: { count: number } }>(
    DISMISS_CLIENT_LOGS_BATCH,
    { ids, dismissed }
  );
  return result.dismissClientLogsBatch?.count ?? 0;
}

/**
 * Create a new support request
 */
export async function createSupportRequest(
  input: CreateSupportRequestInput
): Promise<SupportRequest> {
  const result = await centralGraphQL<{ createSupportRequest: SupportRequest | null }>(
    CREATE_SUPPORT_REQUEST,
    { input }
  );

  if (!result.createSupportRequest) {
    throw new Error('Failed to create support request - authentication may have expired. Please try logging in again.');
  }

  return result.createSupportRequest;
}

/**
 * Get support requests with optional filters and pagination
 */
export async function getSupportRequests(
  filters?: SupportRequestFilters,
  limit = 50,
  offset = 0
): Promise<{ items: SupportRequest[]; total: number }> {
  const result = await centralGraphQL<{
    supportRequests: { items: SupportRequest[]; total: number };
  }>(GET_SUPPORT_REQUESTS, { filters, limit, offset });
  return result.supportRequests ?? { items: [], total: 0 };
}

/**
 * Get a single support request by ID
 */
export async function getSupportRequest(id: string): Promise<SupportRequest | null> {
  const result = await centralGraphQL<{ supportRequest: SupportRequest | null }>(
    GET_SUPPORT_REQUEST,
    { id }
  );
  return result.supportRequest ?? null;
}

/**
 * Update a support request (admin action)
 */
export async function updateSupportRequest(
  id: string,
  input: UpdateSupportRequestInput
): Promise<SupportRequest> {
  const result = await centralGraphQL<{ updateSupportRequest: SupportRequest }>(
    UPDATE_SUPPORT_REQUEST,
    { id, input }
  );
  return result.updateSupportRequest;
}

/**
 * Get support dashboard statistics
 */
export async function getSupportStats(): Promise<SupportStats | null> {
  const result = await centralGraphQL<{ supportStats: SupportStats }>(GET_SUPPORT_STATS);
  return result.supportStats ?? null;
}

/**
 * Delete a support request (owner or admin)
 */
export async function deleteSupportRequest(id: string): Promise<{ success: boolean; message?: string }> {
  const result = await centralGraphQL<{ deleteSupportRequest: { success: boolean; message?: string } }>(
    `mutation DeleteSupportRequest($id: ID!) {
      deleteSupportRequest(id: $id) {
        success
        message
      }
    }`,
    { id }
  );
  return result.deleteSupportRequest ?? { success: false, message: 'Unknown error' };
}

/**
 * Get comments for a support request
 */
export async function getSupportComments(requestId: string): Promise<SupportComment[]> {
  const result = await centralGraphQL<{ supportComments: { items: SupportComment[]; total: number } }>(
    `query GetSupportComments($requestId: ID!) {
      supportComments(requestId: $requestId) {
        items {
          id
          requestId
          userId
          userName
          userEmail
          isAdmin
          content
          createdAt
        }
        total
      }
    }`,
    { requestId }
  );
  return result.supportComments?.items ?? [];
}

/**
 * Add a comment to a support request
 */
export async function createSupportComment(requestId: string, content: string): Promise<SupportComment | null> {
  const result = await centralGraphQL<{ createSupportComment: SupportComment | null }>(
    `mutation CreateSupportComment($input: CreateSupportCommentInput!) {
      createSupportComment(input: $input) {
        id
        requestId
        userId
        userName
        userEmail
        isAdmin
        content
        createdAt
      }
    }`,
    { input: { requestId, content } }
  );
  return result.createSupportComment ?? null;
}

// =============================================================================
// BROWSER INFO HELPER
// =============================================================================

/**
 * Get browser and OS information for support requests
 */
export function getBrowserInfo(): string {
  if (typeof navigator === 'undefined') {
    return 'Server-side';
  }

  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  // Detect browser
  if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
  } else if (ua.includes('Chrome')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari')) {
    browser = 'Safari';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // Detect OS
  if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Mac')) {
    os = 'macOS';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
  }

  return `${browser} on ${os}`;
}
